import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { push } from 'connected-react-router';

import { setAutomationElement } from '@experian-uk/corvetteuk-common-ui';

import { CardBenefits, CardContainer, CardContent, PricePoint, ProductFactSheet } from './productPanel.styles';
import {
  ActionButton,
  ButtonContainer,
  HeaderNumber,
  OptionsCardHeader,
  OptionsCardHeaderContent,
  ActionButtonMessage,
  UpgradeBlockedExplanation,
  UnableToSwitchErrorMessage,
  StyledHeader,
} from './productOptionsCards.styles';
import BlueTick from './blueTick';
import { DownloadIcon } from '../Icons/DownloadIcon';
import updateSession from '../../actions/session/update';
import syncSession from '../../actions/session/sync';
import selectOffer from '../../actions/offers/select';
import tooltipForBenefit from './tooltipForBenefit';
import { products } from '../../constants';
import { clickECDEvent } from '../../pages/home/homePageEcd2';
import { ModalUpsell } from '../../components/ModalUpsell';
import sendReward from '../../actions/conductrics/sendReward';
import GOALS from '../../constants/abTestGoals';
import abTests from '../../constants/abtests';
import OfferBanner from '../ProductPanels/OfferBanner/offerBanner';
import { handleAgentControlVariant } from './utils/handleControlVariant';

@connect(
  state => ({
    error: state.error,
    expiryDate: state.billing.endDate,
    freeOffer: state.offers.basic,
    hasCurrentSubscriptionPlanCode: Boolean(state.subscriptions?.current?.planCode),
    isCoolingOffEligible: (state.subscriptions.current || {}).isCoolingOffEligible,
    conductrics: state.conductrics,
    experienceApiData: state.experienceApi?.experienceApiData,
  }),
  dispatch =>
    bindActionCreators(
      {
        selectOffer,
        push,
        syncSession,
        updateSession,
        sendReward,
      },
      dispatch
    )
)
class ProductOptionCard extends React.Component {
  static propTypes = {
    headerNumber: PropTypes.number.isRequired,
    currentSubscription: PropTypes.string.isRequired,
    fewerPanels: PropTypes.bool.isRequired,
    offer: PropTypes.shape({
      copyLink: PropTypes.string.isRequired,
      benefitCopy: PropTypes.arrayOf(PropTypes.string).isRequired,
      titleText: PropTypes.oneOfType([PropTypes.element, PropTypes.string]).isRequired,
      priceSummary: PropTypes.string.isRequired,
      productName: PropTypes.string.isRequired,
      family: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
    }).isRequired,
    isIdCeFreeTrial: PropTypes.bool.isRequired,
    hasCurrentSubscriptionPlanCode: PropTypes.bool,
    switchPermitted: PropTypes.bool.isRequired,
    selectOffer: PropTypes.func.isRequired,
    updateSession: PropTypes.func.isRequired,
    syncSession: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
    sendReward: PropTypes.func.isRequired,
    isCoolingOffEligible: PropTypes.bool,
    conductrics: PropTypes.shape({
      error: PropTypes.bool,
      fetching: PropTypes.bool,
      sels: PropTypes.objectOf(PropTypes.string),
    }),
    experienceApiData: PropTypes.shape({
      ecdReport: PropTypes.shape({
        conductricsDecisions: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            contentId: PropTypes.string,
            variant: PropTypes.string,
          })
        ),
      }),
      targetedFeatures: PropTypes.arrayOf(
        PropTypes.shape({
          contentId: PropTypes.string,
          variant: PropTypes.string,
        })
      ),
    }),
  };

  static defaultProps = {
    conductrics: {
      error: null,
      fetching: false,
      sels: {},
    },
    experienceApiData: {
      ecdReport: {
        conductricsDecisions: [],
      },
      targetedFeatures: [],
    },
    hasCurrentSubscriptionPlanCode: false,
  };

  state = {
    modalUpsellShowing: false,
  };

  hideModal = () => {
    this.setState(prevState => ({
      modalUpsellShowing: !prevState.modalUpsellShowing,
    }));
  };

  goToSwitch = async () => {
    const { offer } = this.props;
    const identityOfferId = offer.id;
    const identityOfferFamily = offer.family;

    await this.props.selectOffer(identityOfferId, identityOfferFamily);

    this.props.updateSession({
      selectedOfferId: identityOfferId,
      selectedOfferFamily: identityOfferFamily,
    });
    await this.props.syncSession();
    this.props.push('/switch');
  };

  render() {
    const {
      currentSubscription,
      headerNumber,
      offer,
      fewerPanels,
      isIdCeFreeTrial,
      hasCurrentSubscriptionPlanCode,
      switchPermitted,
      isCoolingOffEligible,
    } = this.props;
    const { modalUpsellShowing } = this.state;
    const { sels: selections } = this.props.conductrics || {};
    const conductricsDecisions = this.props.experienceApiData?.ecdReport?.conductricsDecisions || [];

    const homeAATestVariant = selections ? selections[abTests.PRODMOVE_AA_TEST_HP?.apiCode] : null;
    const isVariantB = selections?.[abTests.PRICE_PRODUCT_FACT_SHEET_SWAP.apiCode] === 'B';
    const gridArea = headerNumber !== 1 || fewerPanels ? 'right' : 'middle';
    const isValidVariant = ['A', 'B'].includes(homeAATestVariant);
    const isBreachOrBreachPlus =
      currentSubscription.family === products.breachPlus || currentSubscription.family === products.breach;
    const protectHookFeature = conductricsDecisions.find(f => f.id === abTests.PRODMOVE_PROTECT_HOOK.apiCode);
    const protectHookFeatureVariant = ['B', 'C'].includes(protectHookFeature?.variant);

    const showOfferBanner = offer.family === products.identity && protectHookFeatureVariant;

    if (offer.family === products.identity) {
      handleAgentControlVariant({
        conductricsDecisions,
        agentCode: abTests.PRODMOVE_PROTECT_HOOK.apiCode,
        defaultTestName: abTests.PRODMOVE_PROTECT_HOOK.name,
      });
    }

    const linkToIdentitySwitch = async e => {
      e.preventDefault();

      if (offer.family === products.credit_expert) {
        clickECDEvent('prodmove_switch_to_creditExpert');

        this.setState({
          modalUpsellShowing: true,
        });

        return;
      }

      if (offer.family === products.identity) {
        clickECDEvent('prodmove_switch_to_ID');
        if (isValidVariant) {
          this.props.sendReward(GOALS.SWITCH_TO_IDPLUS_ACCOUNT);
        }
      }

      if (offer.family === products.basic) {
        clickECDEvent('prodmove_switch_to_free', { isCoolingOffEligible });
        if (isValidVariant) {
          this.props.sendReward(GOALS.SWITCH_TO_FREE_ACCOUNT);
        }
      }

      await this.goToSwitch();
    };

    const getCardActionButton = () => {
      if (isIdCeFreeTrial && !hasCurrentSubscriptionPlanCode) {
        return (
          <UpgradeBlockedExplanation {...setAutomationElement('blocked-upgrade-in-trial')}>
            You cannot upgrade as you currently have all CreditExpert features. You will be able to upgrade after your
            trial has ended
          </UpgradeBlockedExplanation>
        );
      }

      if (!switchPermitted) {
        return (
          <React.Fragment>
            <UnableToSwitchErrorMessage {...setAutomationElement('unable-to-switch')}>
              Please try again later or contact us
            </UnableToSwitchErrorMessage>
            <ActionButton {...setAutomationElement('panel-button')} disabled>
              Switch to {offer.productName}
            </ActionButton>
          </React.Fragment>
        );
      }

      return (
        <React.Fragment>
          <ActionButtonMessage>Cancel your {currentSubscription} subscription and</ActionButtonMessage>
          <ActionButton
            noBottomMargin={isVariantB}
            {...setAutomationElement('panel-button')}
            onClick={linkToIdentitySwitch}
          >
            Switch to {offer.productName}
          </ActionButton>
        </React.Fragment>
      );
    };

    return (
      <CardContainer gridLocation={gridArea} {...setAutomationElement(`panel-${offer.family}`)}>
        <OptionsCardHeader>
          {!fewerPanels && <HeaderNumber>{headerNumber}</HeaderNumber>}
          <OptionsCardHeaderContent>
            <h3>{offer.titleText}</h3>
            {isVariantB ? (
              <ProductFactSheetContainer
                isBreachOrBreachPlus={isBreachOrBreachPlus}
                offer={offer}
                priceSheetSwap={isVariantB}
              />
            ) : (
              <PricePointContainer offer={offer} priceStyleSwap={isVariantB} />
            )}
          </OptionsCardHeaderContent>
        </OptionsCardHeader>
        <CardContent>
          {showOfferBanner && <OfferBanner />}
          <CardBenefits family={offer.family}>
            <h3 {...setAutomationElement('benefits-included-title')}>Benefits include:</h3>
            <ul>
              {offer.benefitCopy.map(txt => (
                <li key={txt}>
                  <BlueTick />
                  {txt}
                  {tooltipForBenefit(txt)}
                </li>
              ))}
            </ul>
          </CardBenefits>
        </CardContent>
        <ButtonContainer>{getCardActionButton()}</ButtonContainer>
        {isVariantB ? (
          <PricePointContainer offer={offer} priceStyleSwap={isVariantB} />
        ) : (
          <ProductFactSheetContainer
            isBreachOrBreachPlus={isBreachOrBreachPlus}
            offer={offer}
            priceSheetSwap={isVariantB}
          />
        )}
        <ModalUpsell isShowing={modalUpsellShowing} toggleHide={this.hideModal} credExClick={this.goToSwitch} />
      </CardContainer>
    );
  }
}

const ProductFactSheetContainer = ({ isBreachOrBreachPlus, offer, priceSheetSwap }) => (
  <ProductFactSheet priceSheetSwap={priceSheetSwap} isBreachOrBreachPlus={isBreachOrBreachPlus}>
    <DownloadIcon />
    <a
      {...setAutomationElement(`${offer.family}-panel-product-factsheet-link`)}
      href={offer.copyLink}
      target="_blank"
      rel="noreferrer"
    >
      View {offer.productName} product fact sheet
    </a>
  </ProductFactSheet>
);

ProductFactSheetContainer.propTypes = {
  isBreachOrBreachPlus: PropTypes.bool.isRequired,
  offer: PropTypes.shape({
    copyLink: PropTypes.string.isRequired,
    productName: PropTypes.string.isRequired,
    family: PropTypes.string.isRequired,
  }).isRequired,
  priceSheetSwap: PropTypes.bool.isRequired,
};

const PricePointContainer = ({ offer, priceStyleSwap }) => (
  <React.Fragment>
    <StyledHeader />
    <PricePoint
      {...setAutomationElement(`${offer.family}-price-summary`)}
      priceSheetSwap={priceStyleSwap}
      color={!priceStyleSwap ? '#002556' : '#263B68'}
    >
      {offer.priceSummary || <React.Fragment>&nbsp;</React.Fragment>}
    </PricePoint>
  </React.Fragment>
);

PricePointContainer.propTypes = {
  offer: PropTypes.shape({
    family: PropTypes.string.isRequired,
    priceSummary: PropTypes.string.isRequired,
  }).isRequired,
  priceStyleSwap: PropTypes.bool.isRequired,
};

export default ProductOptionCard;
